import {
  Center,
  Checkbox,
  Flex,
  SimpleGrid,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useFormContext } from "react-hook-form";
import { HiCheck } from "react-icons/hi";
import { IPostBuyNow } from "../../interfaces/PurchaseNow";
import { useAddressByCEP } from "../../hooks";
import { useEffect } from "react";
import {
  FinishStep,
  IMaskInputControlled,
  SectionsForm,
  SelectControlled,
  TextInputControlled,
} from "../../components";
import { useTranslation } from "react-i18next";
interface IProps {
  hasPrice: boolean;
}
const PurchaseStep3 = ({ hasPrice }: IProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useFormContext<IPostBuyNow>();

  const hasDirtySecond = !!watch("plan_id") && !!watch("term_of_product");
  const isTheSameUser = watch("is_the_same_user");
  const document_nr = watch("user.document_nr");
  const name = watch("user.name");
  const email = watch("user.email");

  const handleIsTheSameUser = () => {
    setValue("is_the_same_user", !isTheSameUser);
    if (isTheSameUser) {
      setValue("payment_owner.document_nr", document_nr);
      setValue("payment_owner.name", name);
      setValue("payment_owner.email", email);
    } else {
      setValue("payment_owner.document_nr", "");
      setValue("payment_owner.name", "");
      setValue("payment_owner.email", "");
    }
  };

  useEffect(() => {
    if (!isTheSameUser) {
      setValue("payment_owner.document_nr", document_nr);
      setValue("payment_owner.name", name);
      setValue("payment_owner.email", email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTheSameUser, document_nr, name, email]);

  const hasZipCode = watch("payment_owner.address.zip_code").length === 9;
  const hasErrorZipCode = errors?.payment_owner?.address?.zip_code?.message;
  const { data: dataCep, isError: isErrorCep } = useAddressByCEP({
    cep: watch("payment_owner.address.zip_code"),
  });

  useEffect(() => {
    if (!hasZipCode || isErrorCep) {
      setValue("payment_owner.address.street", "");
      setValue("payment_owner.address.neighborhood", "");
      setValue("payment_owner.address.city", "");
      setValue("payment_owner.address.state", "");
      setValue("payment_owner.address.country", "");
      setValue("payment_owner.address.additional_details", "");
      setValue("payment_owner.address.number", "");
    } else if (!!dataCep) {
      setValue("payment_owner.address.street", dataCep?.street);
      setValue("payment_owner.address.neighborhood", dataCep?.neighborhood);
      setValue("payment_owner.address.city", dataCep?.city);
      setValue("payment_owner.address.state", dataCep?.state);
      setValue("payment_owner.address.country", dataCep?.country);
      setValue("payment_owner.address.additional_details", "");
      setValue("payment_owner.address.number", "");
    }
    if (isErrorCep) {
      setError("payment_owner.address.zip_code", { message: "CEP inválido" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCep, hasZipCode, isErrorCep]);

  const styleSpecialDisabled = {
    input: {
      ":disabled": {
        backgroundColor: "white",
        color: "black",
        cursor: "not-allowed",
      },
    },
    wrapper: {
      ":disabled": {
        backgroundColor: "white",
        color: "black",
        cursor: "not-allowed",
      },
    },
  };
  return (
    <SectionsForm
      title={t("marketplace.comprar_agora.purchase_step_3.title_section")}
      number={"03"}
    >
      {hasDirtySecond ? (
        hasPrice ? (
          <>
            <Flex direction={"column"} gap={24}>
              <Text
                fz={{ base: 24 }}
                fw={{ base: 600 }}
                lh={{ base: "37.2px" }}
                c={"basic.5"}
              >
                {t("marketplace.comprar_agora.purchase_step_3.credit_card")}
              </Text>
              <Checkbox
                color="primary.1"
                size="md"
                icon={HiCheck}
                onChange={handleIsTheSameUser}
                checked={isTheSameUser}
                label={
                  <Text>
                    {t("marketplace.comprar_agora.purchase_step_3.other_buyer")}
                  </Text>
                }
              />

              <SimpleGrid
                cols={1}
                breakpoints={[
                  { minWidth: "991", cols: 2, spacing: "32px" },
                  { minWidth: "1600", cols: 3, spacing: "32px" },
                ]}
                spacing="32px"
                verticalSpacing="24px"
              >
                {isTheSameUser ? (
                  <>
                    <TextInputControlled
                      name="payment_owner.name"
                      label={t(
                        "marketplace.comprar_agora.purchase_step_3.name_other_buyer"
                      )}
                      placeholder={t(
                        "marketplace.comprar_agora.purchase_step_3.name_placeholder_other_buyer"
                      )}
                      withAsterisk
                    />
                    <TextInputControlled
                      name="payment_owner.email"
                      label={t(
                        "marketplace.comprar_agora.purchase_step_3.email_other_buyer"
                      )}
                      withAsterisk
                      placeholder={t(
                        "marketplace.comprar_agora.purchase_step_3.email_placeholder_other_buyer"
                      )}
                    />
                    <IMaskInputControlled
                      name="payment_owner.document_nr"
                      label={t(
                        "marketplace.comprar_agora.purchase_step_3.cpf_other_buyer"
                      )}
                      placeholder="000.000.000-00"
                      mask="000.000.000-00"
                      withAsterisk
                    />
                  </>
                ) : null}
                <SelectControlled
                  data={["Visa", "Mastercard", "American Express"]}
                  name="payment_info.issuer"
                  label={t("global.input.issuer_card.label")}
                  placeholder={t("global.input.issuer_card.label")}
                  withAsterisk
                />
                <IMaskInputControlled
                  name="payment_info.number"
                  label={t("global.input.number_card.label")}
                  placeholder={t("global.input.number_card.label")}
                  mask="0000 0000 0000 0000"
                  withAsterisk
                />
                <TextInputControlled
                  name="payment_info.name"
                  label={t("global.input.name_card.label")}
                  placeholder={t("global.input.name_card.label")}
                  withAsterisk
                />
                <IMaskInputControlled
                  name="payment_info.maturity"
                  label={t("global.input.expiration_card.label")}
                  placeholder={t("global.input.expiration_card.label")}
                  mask="00/00"
                  withAsterisk
                />
                <IMaskInputControlled
                  name="payment_info.cvv"
                  label={t("global.input.cvv_card.label")}
                  placeholder={t("global.input.cvv_card.label")}
                  mask="000"
                  withAsterisk
                />
              </SimpleGrid>
            </Flex>
            <Flex direction={"column"} gap={24}>
              <Text fw={600} size={20} color={theme.colors.basic[5]}>
                {t("marketplace.comprar_agora.purchase_step_3.address")}
              </Text>

              <SimpleGrid
                cols={1}
                breakpoints={[
                  { minWidth: "991", cols: 2, spacing: "32px" },
                  { minWidth: "1600", cols: 3, spacing: "32px" },
                ]}
              >
                <IMaskInputControlled
                  name="payment_owner.address.zip_code"
                  label={t("global.input.cep.label")}
                  placeholder="00000-000"
                  mask="00000-000"
                  withAsterisk
                />
                <Tooltip
                  label={t("global.input.cep.tooltip")}
                  withArrow
                  position="top-start"
                >
                  <span>
                    <TextInputControlled
                      name="payment_owner.address.state"
                      label={t("global.input.state.label")}
                      placeholder={
                        isErrorCep
                          ? t("global.input.cep.placeholder_error_inputs")
                          : t("global.input.cep.placeholder_other_inputs")
                      }
                      withAsterisk
                      disabled={true}
                      width={"100%"}
                      sx={
                        !isErrorCep && !hasErrorZipCode && hasZipCode
                          ? styleSpecialDisabled
                          : {}
                      }
                    />
                  </span>
                </Tooltip>
                <Tooltip
                  label={t("global.input.cep.tooltip")}
                  withArrow
                  position="top-start"
                  aria-atomic
                >
                  <span>
                    <TextInputControlled
                      name="payment_owner.address.city"
                      label={t("global.input.city.label")}
                      placeholder={
                        isErrorCep
                          ? t("global.input.cep.placeholder_error_inputs")
                          : t("global.input.cep.placeholder_other_inputs")
                      }
                      withAsterisk
                      disabled={true}
                      width={"100%"}
                      sx={
                        !isErrorCep && !hasErrorZipCode && hasZipCode
                          ? styleSpecialDisabled
                          : {}
                      }
                    />
                  </span>
                </Tooltip>

                <TextInputControlled
                  name="payment_owner.address.neighborhood"
                  label={t("global.input.neighborhood.label")}
                  withAsterisk
                  placeholder={
                    isErrorCep
                      ? t("global.input.cep.placeholder_error_inputs")
                      : hasZipCode
                      ? t("global.input.neighborhood.placeholder")
                      : t("global.input.cep.placeholder_other_inputs")
                  }
                  disabled={isErrorCep || !hasZipCode}
                />

                <TextInputControlled
                  name="payment_owner.address.street"
                  label={t("global.input.street.label")}
                  withAsterisk
                  placeholder={
                    isErrorCep
                      ? t("global.input.cep.placeholder_error_inputs")
                      : hasZipCode
                      ? t("global.input.street.placeholder")
                      : t("global.input.cep.placeholder_other_inputs")
                  }
                  disabled={isErrorCep || !hasZipCode}
                />

                <TextInputControlled
                  name="payment_owner.address.number"
                  label={t("global.input.number.label")}
                  withAsterisk
                  placeholder={
                    isErrorCep
                      ? t("global.input.cep.placeholder_error_inputs")
                      : hasZipCode
                      ? t("global.input.number.placeholder")
                      : t("global.input.cep.placeholder_other_inputs")
                  }
                  disabled={isErrorCep || !hasZipCode}
                />

                <TextInputControlled
                  name="payment_owner.address.additional_details"
                  label={t("global.input.additional_address.label")}
                  placeholder={
                    isErrorCep
                      ? t("global.input.cep.placeholder_error_inputs")
                      : hasZipCode
                      ? t("global.input.additional_address.placeholder")
                      : t("global.input.cep.placeholder_other_inputs")
                  }
                  disabled={isErrorCep || !hasZipCode}
                />
              </SimpleGrid>
            </Flex>
          </>
        ) : (
          <Center>
            {t("marketplace.comprar_agora.purchase_step_3.no_value")}
          </Center>
        )
      ) : (
        <FinishStep />
      )}
    </SectionsForm>
  );
};
export default PurchaseStep3;
